import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import PaywallBanner from "../components/PaywallBanner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

export default function Team() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [seats, setSeats] = useState({ seats_used: 0, seats_included: 3, can_invite: false, subscription_active: false });
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, i, s] = await Promise.all([
        api.get("/agency/team/members"),
        api.get("/agency/team/invites"),
        api.get("/agency/team/seats"),
      ]);
      setMembers(m.data);
      setInvites(i.data);
      setSeats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const createInvite = async () => {
    setBusy(true);
    try {
      await api.post("/agency/team/invites", inviteEmail.trim() ? { email: inviteEmail.trim() } : {});
      toast.success(t("team.invite_created"));
      setInviteEmail("");
      setShowInvite(false);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "SEAT_CAP_REACHED") toast.error(t("team.seat_cap_reached"));
      else toast.error(formatApiErrorDetail(detail) || t("common.error_generic"));
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm(t("team.confirm_revoke"))) return;
    try {
      await api.post(`/agency/team/invites/${id}/revoke`);
      toast.success(t("team.invite_revoked"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    }
  };

  const removeMember = async (id, name) => {
    if (!window.confirm(t("team.confirm_remove_member", { name }))) return;
    try {
      await api.delete(`/agency/team/members/${id}`);
      toast.success(t("team.member_removed"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    }
  };

  const copyInvite = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      toast.success(t("team.copied"));
    }).catch(() => toast.error(t("common.error_generic")));
  };

  if (!user) return null;

  const disableInvite = !seats.can_invite;
  const inviteBlockReason = !seats.subscription_active
    ? "no_subscription"
    : seats.seats_used >= seats.seats_included
    ? "seat_cap"
    : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="team-page">
      <PaywallBanner />
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("team.eyebrow")}</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl" data-testid="team-title">
              {t("team.title")}
            </h1>
            <p className="mt-2 text-sm text-slate-600" data-testid="team-seat-counter">
              {t("team.seats_used", { used: seats.seats_used, included: seats.seats_included })}
            </p>
          </div>
          <button
            onClick={() => setShowInvite(true)}
            disabled={disableInvite}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
            data-testid="team-invite-cta"
            title={inviteBlockReason ? t(`team.block_${inviteBlockReason}`) : ""}
          >
            {t("team.invite_cta")}
          </button>
        </div>

        {inviteBlockReason && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="team-invite-block-hint">
            {inviteBlockReason === "no_subscription" ? (
              <>
                {t("team.block_no_subscription")} <Link to="/settings/subscription" className="font-semibold underline">{t("team.block_go_subscribe")}</Link>
              </>
            ) : (
              <>{t("team.block_seat_cap")}</>
            )}
          </div>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="team-members">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("team.members_title")}</h2>
          {loading ? (
            <p className="mt-3 text-sm text-slate-500">{t("common.loading")}</p>
          ) : members.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">{t("team.members_empty")}</p>
          ) : (
            <table className="mt-3 w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 text-left">{t("team.col_name")}</th>
                  <th className="py-2 text-left">{t("team.col_email")}</th>
                  <th className="py-2 text-left">{t("team.col_joined")}</th>
                  <th className="py-2 text-right">{t("team.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100" data-testid={`member-row-${m.id}`}>
                    <td className="py-2 text-slate-900">{m.name}{m.is_self && <span className="ml-1 text-xs text-indigo-700">({t("team.you")})</span>}</td>
                    <td className="py-2 text-slate-600">{m.email}</td>
                    <td className="py-2 text-slate-500">{formatDateShort(m.joined_at)}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => removeMember(m.id, m.name)}
                        disabled={m.is_self || members.length <= 1}
                        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`member-remove-${m.id}`}
                      >
                        {t("team.remove")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="team-invites">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("team.invites_title")}</h2>
          {invites.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500" data-testid="team-invites-empty">{t("team.invites_empty")}</p>
          ) : (
            <table className="mt-3 w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 text-left">{t("team.col_email")}</th>
                  <th className="py-2 text-left">{t("team.col_created")}</th>
                  <th className="py-2 text-left">{t("team.col_expires")}</th>
                  <th className="py-2 text-left">{t("team.col_status")}</th>
                  <th className="py-2 text-right">{t("team.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const used = !!inv.used_at;
                  const expired = new Date(inv.expires_at) < new Date();
                  return (
                    <tr key={inv.id} className="border-t border-slate-100" data-testid={`invite-row-${inv.id}`}>
                      <td className="py-2 text-slate-700">{inv.email || <span className="text-slate-400">—</span>}</td>
                      <td className="py-2 text-slate-500">{formatDateShort(inv.created_at)}</td>
                      <td className="py-2 text-slate-500">{formatDateShort(inv.expires_at)}</td>
                      <td className="py-2">
                        {used ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">{t("team.status_used")}</span> :
                        expired ? <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-100">{t("team.status_expired")}</span> :
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">{t("team.status_active")}</span>}
                      </td>
                      <td className="py-2 text-right">
                        {!used && !expired && (
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => copyInvite(inv.invite_url, inv.id)}
                              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                              data-testid={`invite-copy-${inv.id}`}
                            >
                              {t("team.copy_url")}
                            </button>
                            <button
                              onClick={() => revoke(inv.id)}
                              className="rounded border border-rose-200 bg-white px-2 py-0.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                              data-testid={`invite-revoke-${inv.id}`}
                            >
                              {t("team.revoke")}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p className="mt-3 text-xs text-slate-500">{t("team.invites_hint")}</p>
        </section>

        {showInvite && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="team-invite-modal">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900">{t("team.invite_modal_title")}</h3>
              <p className="mt-1 text-sm text-slate-600">{t("team.invite_modal_body")}</p>
              <label className="mt-4 block text-sm font-medium text-slate-700">
                {t("team.invite_email_optional")}
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                placeholder="teammate@example.com"
                data-testid="invite-modal-email"
              />
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setShowInvite(false)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="invite-modal-cancel">
                  {t("common.cancel")}
                </button>
                <button
                  onClick={createInvite}
                  disabled={busy}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                  data-testid="invite-modal-submit"
                >
                  {busy ? t("common.loading") : t("team.invite_generate")}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
